import React from 'react';
import { LineDiff, WordDiff } from '@hooks/useDiff';
import { CompareViewMode } from '@/types/compare.types';

interface DiffRendererProps {
  lineDiffs: LineDiff[];
  viewMode: CompareViewMode;
  showLineNumbers?: boolean;
  className?: string;
}

const WordDiffContent: React.FC<{ wordDiffs: WordDiff[]; showOnly?: 'added' | 'removed' | 'all' }> = ({ 
  wordDiffs, 
  showOnly = 'all' 
}) => (
  <>
    {wordDiffs.map((wd, i) => {
      if (showOnly === 'removed' && wd.status === 'added') return null;
      if (showOnly === 'added' && wd.status === 'removed') return null;
      return <span key={i} className={`word-diff ${wd.status}`}>{wd.text}</span>;
    })}
  </>
);

const DiffLine: React.FC<{ line: LineDiff; showLineNumbers: boolean; viewMode: CompareViewMode }> = ({ line, showLineNumbers, viewMode }) => {
  const getInd = () => {
    if (line.type === 'added') return '+';
    if (line.type === 'removed') return '−';
    if (line.type === 'modified') return '~';
    return ' ';
  };

  if (viewMode === 'unified' && line.type === 'modified') {
    return (
      <>
        <div className={`diff-line removed ${line.isStructural ? 'structural' : ''}`}>
          {showLineNumbers && <><span className="line-number old">{line.lineNumber.a !== null ? line.lineNumber.a + 1 : ''}</span><span className="line-number new"></span></>}
          <span className="line-indicator">−</span>
          <span className="line-content">{line.wordDiffs ? <WordDiffContent wordDiffs={line.wordDiffs} showOnly="removed" /> : <span className="line-text removed">{line.lineA}</span>}</span>
        </div>
        <div className="diff-line added">
          {showLineNumbers && <><span className="line-number old"></span><span className="line-number new">{line.lineNumber.b !== null ? line.lineNumber.b + 1 : ''}</span></>}
          <span className="line-indicator">+</span>
          <span className="line-content">{line.wordDiffs ? <WordDiffContent wordDiffs={line.wordDiffs} showOnly="added" /> : <span className="line-text added">{line.lineB}</span>}</span>
        </div>
      </>
    );
  }

  return (
    <div className={`diff-line ${line.type} ${line.isStructural ? 'structural' : ''}`}>
      {showLineNumbers && (
        <>
          <span className="line-number old">{line.lineNumber.a !== null ? line.lineNumber.a + 1 : ''}</span>
          <span className="line-number new">{line.lineNumber.b !== null ? line.lineNumber.b + 1 : ''}</span>
        </>
      )}
      <span className="line-indicator">{getInd()}</span>
      <span className="line-content">
        {line.type === 'modified' && line.wordDiffs ? <WordDiffContent wordDiffs={line.wordDiffs} /> :
         line.type === 'removed' ? <span className="line-text removed">{line.lineA}</span> :
         line.type === 'added' ? <span className="line-text added">{line.lineB}</span> :
         <span className="line-text">{line.lineA || line.lineB}</span>}
      </span>
    </div>
  );
};

export const DiffRenderer: React.FC<DiffRendererProps> = ({ lineDiffs, viewMode, showLineNumbers = true, className = '' }) => {
  return (
    <div className={`diff-renderer ${viewMode} ${className}`}>
      <div className="diff-table">
        {lineDiffs.map((line, idx) => <DiffLine key={idx} line={line} showLineNumbers={showLineNumbers} viewMode={viewMode} />)}
      </div>
      <style>{`
        .diff-renderer { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 0.8rem; line-height: 1.6; background: #0d0d1a; border-radius: 6px; overflow: hidden; }
        .diff-table { display: flex; flex-direction: column; }
        .diff-line { display: flex; min-height: 1.6em; border-bottom: 1px solid #1a1a2e; }
        .diff-line.added { background: rgba(100, 255, 100, 0.08); }
        .diff-line.removed { background: rgba(255, 100, 100, 0.08); }
        .diff-line.modified { background: rgba(255, 200, 100, 0.08); }
        .diff-line.structural { background: rgba(100, 150, 255, 0.1); font-weight: 600; }
        .line-number { width: 3rem; padding: 0 0.5rem; text-align: right; color: #444; background: #0a0a15; user-select: none; border-right: 1px solid #1a1a2e; }
        .line-indicator { width: 1.5rem; text-align: center; font-weight: 700; }
        .diff-line.added .line-indicator { color: #69db7c; }
        .diff-line.removed .line-indicator { color: #ff6b6b; }
        .line-content { flex: 1; padding: 0 0.75rem; white-space: pre-wrap; word-break: break-all; }
        .line-text.removed { color: #ff8888; }
        .line-text.added { color: #88ff88; }
        .word-diff.removed { background: rgba(255, 100, 100, 0.25); color: #ff6b6b; text-decoration: line-through; }
        .word-diff.added { background: rgba(100, 255, 100, 0.25); color: #69db7c; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default DiffRenderer;
